import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { adminAPI } from '../services/api';
import { setCredentials } from '../authSlice';
import { CheckCircleFill, ExclamationTriangleFill, Eye, EyeSlash } from 'react-bootstrap-icons';
import AdminIcon from '../img/Admin.gif';
import '../CSS/AdminPanel.css';

const AdminPanel = () => {
  const [adminData, setAdminData] = useState({
    name: '',
    email: '',
    position: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success'
  });
  const dispatch = useDispatch();
const showToast = (message, variant = 'success') => {
  setToast({ show: true, message, variant });
};

useEffect(() => {
  if (!toast.show) return;
  const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  return () => clearTimeout(t);
}, [toast.show]);

const fetchAdminInfo = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await adminAPI.getAdminInfo(token);
    setAdminData(prev => ({
      ...prev,
      name: response.data.name || '',
      email: response.data.email || '',
      position: response.data.position || '',
      phone: response.data.phone || ''
    }));
  } catch (error) {
    showToast(error.response?.data?.message || 'Failed to fetch data. Please login again', 'danger');
  }
};

  useEffect(() => {
    fetchAdminInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdminData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateProfileForm = () => {
    const newErrors = {};
    if (!adminData.name) newErrors.name = 'Name is required';
    if (!adminData.email) newErrors.email = 'Email is required';
    if (!adminData.phone) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    if (!adminData.currentPassword) newErrors.currentPassword = 'Current password required';
    if (!adminData.newPassword) newErrors.newPassword = 'New password required';
    else if (adminData.newPassword.length < 8) newErrors.newPassword = 'Minimum 8 characters';
    else if (adminData.newPassword === adminData.currentPassword) newErrors.newPassword = 'New password must be different';
    if (adminData.newPassword !== adminData.confirmPassword) newErrors.confirmPassword = 'Passwords must match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleProfileSubmit = async (e) => {
  e.preventDefault();
  if (!validateProfileForm()) return;

  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const response = await adminAPI.updateProfile(token, {
      name: adminData.name,
      email: adminData.email,
      position: adminData.position,
      phone: adminData.phone
    });

    showToast('Profile updated successfully');
    
    // Update Redux store and localStorage if token changed
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      dispatch(setCredentials({
        user: {
          name: response.data.user.name,
          email: response.data.user.email,
          role: 'admin'
        },
        token: response.data.token
      }));
    }

    // Refresh data with the new token
    await fetchAdminInfo();
  } catch (error) {
    showToast(error.response?.data?.message || 'Update failed', 'danger');
  } finally {
    setLoading(false);
  }
};

  const handlePasswordSubmit = async (e) => {
  e.preventDefault();
  if (!validatePasswordForm()) return;

  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    await adminAPI.changePassword(token, {
      currentPassword: adminData.currentPassword,
      newPassword: adminData.newPassword
    });

    showToast('Password updated successfully');
    setAdminData(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  } catch (error) {
    showToast(error.response?.data?.message || 'Password change failed', 'danger');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="container-fluid"  >
      <div className="row" >
        <div className="dashboard d-flex flex-wrap" >
          
          <div className="dashboard-Container col" >
            <div className="admin-panel-container  container-fluid" >
              <div className="row my-4">
                <div className="col-12 page-header">
                  <h3>Admin Panel<img className="animated-icons" src={AdminIcon} alt="Admin icon"/></h3>
                </div>
              </div>

              <ul className="nav nav-tabs mb-4 ">
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                  >
                    Profile
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                    onClick={() => setActiveTab('password')}
                  >
                    Change Password
                  </button>
                </li>
              </ul>

              <div className="row">
                <div className="col-lg-12">
                  {activeTab === 'profile' ? (
                    <div className="card">
                      <div className="card-header">
                        <h4>Profile Information</h4>
                      </div>
                      <div className="card-body">
                      <form onSubmit={handleProfileSubmit}>
  <div className="row">
    {/* Name Field */}
    <div className="col-md-6">
      <div className="form-floating-group">
        <input
          type="text"
          className={`form-control shadow p-3 mb-5 rounded ${errors.name ? 'is-invalid' : ''}`}
          name="name"
          id="nameInput"
          value={adminData.name}
          onChange={handleChange}
          placeholder=" "
        />
        <label className="form-label" htmlFor="nameInput">Name</label>
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
      </div>
    </div>

    {/* Email Field */}
    <div className="col-md-6">
      <div className="form-floating-group">
        <input
          type="email"
          className={`form-control shadow p-3 mb-5 rounded ${errors.email ? 'is-invalid' : ''}`}
          name="email"
          id="emailInput"
          value={adminData.email}
          onChange={handleChange}
          placeholder=" "
        />
        <label className="form-label" htmlFor="emailInput">Email</label>
        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
      </div>
    </div>
  </div>

  <div className="row">
    {/* Position Field */}
    <div className="col-md-6">
      <div className="form-floating-group">
        <input
          type="text"
          className="form-control shadow p-3 mb-5 rounded"
          name="position"
          id="positionInput"
          value={adminData.position}
          onChange={handleChange}
          placeholder=" "
        />
        <label className="form-label" htmlFor="positionInput">Position</label>
      </div>
    </div>

    {/* Phone Field */}
    <div className="col-md-6">
      <div className="form-floating-group">
        <input
          type="text"
          className={`form-control shadow p-3 mb-5 rounded ${errors.phone ? 'is-invalid' : ''}`}
          name="phone"
          id="phoneInput"
          value={adminData.phone}
          onChange={handleChange}
          placeholder=" "
        />
        <label className="form-label" htmlFor="phoneInput">Phone Number</label>
        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
      </div>
    </div>
  </div>

  <button 
    type="submit" 
    className="btn btn-custom mt-3"
    disabled={loading}
  >
    {loading ? 'Updating...' : 'Update Profile'}
  </button>
</form>
                      </div>
                    </div>
                  ) : (
                    <div className="card">
                      <div className="card-header">
                        <h4>Change Password</h4>
                      </div>
                      <div className="card-body">
                      <form onSubmit={handlePasswordSubmit} className="small-form">
  {/* Current Password */}
  <div className="form-floating-group password-input-container">
    <input
      type={showPassword.current ? "text" : "password"}
      className={`form-control shadow p-3 mb-5 rounded ${errors.currentPassword ? 'is-invalid' : ''}`}
      name="currentPassword"
      id="currentPasswordInput"
      value={adminData.currentPassword}
      onChange={handleChange}
      placeholder=" "
    />
    <label className="form-label" htmlFor="currentPasswordInput">Current Password</label>
    <button 
      className="password-toggle-btn"
      type="button"
      onClick={() => togglePasswordVisibility('current')}
    >
      {showPassword.current ? <EyeSlash /> : <Eye />}
    </button>
    {errors.currentPassword && <div className="invalid-feedback">{errors.currentPassword}</div>}
  </div>

  {/* New Password */}
  <div className="form-floating-group password-input-container">
    <input
      type={showPassword.new ? "text" : "password"}
      className={`form-control shadow p-3 mb-5 rounded ${errors.newPassword ? 'is-invalid' : ''}`}
      name="newPassword"
      id="newPasswordInput"
      value={adminData.newPassword}
      onChange={handleChange}
      placeholder=" "
    />
    <label className="form-label" htmlFor="newPasswordInput">New Password</label>
    <button 
      className="password-toggle-btn"
      type="button"
      onClick={() => togglePasswordVisibility('new')}
    >
      {showPassword.new ? <EyeSlash /> : <Eye />}
    </button>
    {errors.newPassword && <div className="invalid-feedback">{errors.newPassword}</div>}
  </div>

  {/* Confirm Password */}
  <div className="form-floating-group password-input-container">
    <input
      type={showPassword.confirm ? "text" : "password"}
      className={`form-control shadow p-3 mb-5 rounded ${errors.confirmPassword ? 'is-invalid' : ''}`}
      name="confirmPassword"
      id="confirmPasswordInput"
      value={adminData.confirmPassword}
      onChange={handleChange}
      placeholder=" "
    />
    <label className="form-label" htmlFor="confirmPasswordInput">Confirm New Password</label>
    <button 
      className="password-toggle-btn"
      type="button"
      onClick={() => togglePasswordVisibility('confirm')}
    >
      {showPassword.confirm ? <EyeSlash /> : <Eye />}
    </button>
    {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
  </div>

  <button 
    type="submit" 
    className="btn btn-custom mt-3"
    disabled={loading}
  >
    {loading ? 'Updating...' : 'Update Password'}
  </button>
</form>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    <div className="adp-toast-region" aria-live="polite" aria-atomic="true">
  <div
    className={`adp-toast adp-toast-${toast.variant} ${toast.show ? 'adp-toast-show' : ''}`}
    role="alert"
  >
    <div className="adp-toast-header">
      <span className="adp-toast-title">
        {toast.variant === 'success' ? 'Success' : 'Error'}
      </span>
      <button
        className="adp-toast-close"
        onClick={() => setToast(p => ({ ...p, show: false }))}
        aria-label="Close"
      >×</button>
    </div>
    <div className="adp-toast-body">
      {toast.variant === 'success'
        ? <CheckCircleFill className="adp-toast-icon" />
        : <ExclamationTriangleFill className="adp-toast-icon" />}
      {toast.message}
    </div>
  </div>
</div>
    </div>
  );
};

export default AdminPanel;